/**
 * Abbonamenti — la sezione che raccoglie tutto quello che si incassa a cadenza
 * fissa. Il file si chiama ancora ServicesPage perche' la rotta e' /services e
 * i segnalibri vecchi devono continuare a funzionare: a cambiare e' il nome che
 * si legge, non l'indirizzo.
 *
 * Tre schede, in ordine di quanto servono:
 *  1. Panoramica — quanto entra, cosa si rinnova, cosa non torna. E' la prima
 *     perche' nove volte su dieci si entra qui per quello.
 *  2. Elenco — la lista filtrabile, una riga per abbonamento.
 *  3. Fuori Stripe — i canoni che si fatturano a mano. Senza questa scheda la
 *     sezione racconterebbe un quarto del giro d'affari ricorrente spacciandolo
 *     per il totale. Dice quanto entra e quando, non che lavoro e': quasi tutti
 *     quei canoni sono commesse pagate a rate, e la commessa si legge in
 *     Progetti, dove ci sono oggetto, valore e scadenza presi dal preventivo.
 *     Ogni riga ci rimanda, cosi' la stessa cosa non viene raccontata due volte.
 *
 * Stato in URL (24/9): scheda · q · ordina · stato · tipo · solo, cosi' un
 * link riapre l'elenco com'era. L'elenco e' paginato: ordina l'API (`sort`).
 * Stato e tipo erano due tendine in barra, le "lenti" della panoramica uno
 * stato nascosto: adesso stanno tutti in Filtra per, coi numeri accanto.
 */
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { NewMenu } from '../../components/ui/NewMenu';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { SkeletonKPIRow, SkeletonTable, SkeletonCard } from '../../components/ui/Skeleton';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  ManagedServiceType,
  ServiceStatus,
  SERVICE_STATUS_LABEL,
  MANAGED_SERVICE_LABEL,
} from '@masamune/shared';
import { SubscriptionsOverview } from './SubscriptionsOverview';
import { OffStripeTab } from './OffStripeTab';
import type { Overview } from './subscriptions.types';
import { useIsMobile } from '../../hooks/useIsMobile';
import { AbbonamentiMobile } from './AbbonamentiMobile';
import './subscriptions.css';

interface ManagedService {
  id: string;
  type: keyof typeof ManagedServiceType;
  provider: 'STRIPE' | 'HIGHLEVEL' | 'MANUAL';
  status: keyof typeof ServiceStatus;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  planCode: string;
  planLabel: string;
  amount: number;
  listAmount: number;
  costAmount: number;
  currency: string;
  externalId: string | null;
  externalUrl: string | null;
  startedAt: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  lastSyncedAt: string | null;
  metadata: Record<string, unknown>;
  // Nullable: un abbonamento importato da Stripe che non e' stato collegato a
  // nessuna anagrafica con certezza resta scollegato (meglio un buco visibile
  // che un collegamento inventato).
  client: { id: string; legalName: string; shortName: string } | null;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedServices {
  items: ManagedService[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

type Scheda = 'panoramica' | 'elenco' | 'fuori';
const SCHEDE_VALIDE: Scheda[] = ['panoramica', 'elenco', 'fuori'];

type Lente = 'senza-cliente' | 'scontati' | 'scaduti' | 'gratis';
const LENTI: Lente[] = ['gratis', 'scaduti', 'scontati', 'senza-cliente'];

const TIPI: Array<{ key: keyof typeof ManagedServiceType; label: string }> = [
  { key: 'HOSTING', label: 'Hosting Masamune' },
  { key: 'GODEAL',  label: 'GoDeal' },
  { key: 'AURA_OS', label: 'Aura OS' },
  { key: 'CUSTOM',  label: 'Servizi su misura' },
];

/** Ordina per: la lista e' paginata, ordina l'API. `stato` e' l'ordine di prima. */
const ORDINI: OpzioneOrdina[] = [
  { key: 'stato', label: 'Prima gli attivi, in fondo i cancellati' },
  { key: 'rinnovo', label: 'Prossimo rinnovo prima' },
  { key: 'costosi', label: 'Prima i più costosi al mese' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];

/** In quest'ordine: i pagamenti non riusciti sono la cosa da guardare subito. */
const STATI: Array<{ key: keyof typeof ServiceStatus; label: string; conta: keyof Overview['counts'] }> = [
  { key: 'ACTIVE',    label: 'Solo gli attivi',                        conta: 'active' },
  { key: 'PAST_DUE',  label: 'Solo quelli col pagamento non riuscito', conta: 'pastDue' },
  { key: 'TRIALING',  label: 'Solo quelli in prova gratuita',          conta: 'trialing' },
  { key: 'PAUSED',    label: 'Solo quelli in pausa',                   conta: 'paused' },
  { key: 'CANCELLED', label: 'Solo i cancellati',                      conta: 'cancelled' },
  { key: 'EXPIRED',   label: 'Solo gli scaduti',                       conta: 'expired' },
];

const LENTE_LABEL: Record<Lente, string> = {
  gratis: 'Solo gli attivi che non incassano niente',
  scaduti: 'Solo gli attivi col rinnovo già passato',
  scontati: 'Solo quelli con uno sconto permanente',
  'senza-cliente': 'Solo gli attivi senza cliente in rubrica',
};

function statusChipTone(s: ManagedService['status']): 'ok' | 'info' | 'warn' | 'alert' | 'ghost' {
  switch (s) {
    case 'ACTIVE':    return 'ok';
    case 'TRIALING':  return 'info';
    case 'PAST_DUE':  return 'warn';
    case 'PAUSED':    return 'ghost';
    case 'CANCELLED': return 'alert';
    case 'EXPIRED':   return 'alert';
  }
}

/** "ogni quanto" in parole: nella riga di un elenco "Mese" da solo non si capisce. */
function cicloLabel(c: ManagedService['billingCycle']): string {
  return c === 'MONTHLY' ? 'ogni mese' : c === 'QUARTERLY' ? 'ogni 3 mesi' : "ogni anno";
}

/**
 * Sul telefono la pagina è AbbonamentiMobile (quanto incassiamo al mese in
 * grande, poi rinnovi, tutti, fuori Stripe). Sul computer resta questa.
 */
export function ServicesPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><AbbonamentiMobile /></div> : <ServicesDesktop />;
}

function ServicesDesktop() {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const qc = useQueryClient();

  const [, setSearchParams] = useSearchParams();
  const [schedaParam, setScheda] = useUrlState<Scheda>('scheda', 'panoramica');
  const scheda: Scheda = SCHEDE_VALIDE.includes(schedaParam) ? schedaParam : 'panoramica';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const importStripe = useMutation({
    mutationFn: () => api.post<{ scanned: number; created: number; updated: number; skippedNoClient: number }>('/services/import-from-stripe'),
    onSuccess: (r) => {
      toast.ok('Import Stripe completato', `Scansionate ${r.scanned} sub · ${r.created} create · ${r.updated} aggiornate · ${r.skippedNoClient} senza cliente`);
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Errore import Stripe', err instanceof ApiError ? err.message : 'Verifica che STRIPE_SECRET_KEY sia configurato.'),
  });

  const { data: ov, isLoading: ovLoading } = useQuery({
    queryKey: ['services', 'overview'],
    queryFn: () => api.get<Overview>('/services/overview'),
  });

  // ── Ordina e filtra ──
  // I numeri accanto alle voci arrivano dalla panoramica, che li ha gia' contati
  // su tutti gli abbonamenti.
  const ordina = useOrdina(ORDINI);
  const filtriDefs = useMemo<FiltroDef[]>(() => [
    {
      param: 'stato', label: "Stato dell'abbonamento",
      opzioni: STATI.map((s) => ({ value: s.key, label: s.label, count: ov?.counts[s.conta] })),
    },
    {
      param: 'tipo', label: 'Tipo di servizio',
      opzioni: TIPI.map((t) => ({ value: t.key, label: t.label, count: ov ? (ov.byType.find((b) => b.type === t.key)?.total ?? 0) : undefined })),
    },
    {
      // Le viste "storte": non sono stati di Stripe, sono domande che ci si fa
      // guardando la panoramica ("quali sono quelli senza cliente?").
      param: 'solo', label: 'Da controllare',
      opzioni: LENTI.map((l) => ({
        value: l,
        label: LENTE_LABEL[l],
        count: !ov ? undefined
          : l === 'gratis' ? ov.attention.zeroRevenue.length
          : l === 'scaduti' ? ov.attention.overdue.length
          : l === 'scontati' ? ov.attention.discounted.length
          : ov.attention.noClientActive.length,
      })),
    },
  ], [ov]);
  const filtri = useFiltri(filtriDefs);
  const tipo = filtri.valore('tipo');
  const stato = filtri.valore('stato');
  const lenteParam = filtri.valore('solo');
  const lente: Lente | '' = (LENTI as string[]).includes(lenteParam) ? (lenteParam as Lente) : '';

  // Cambiando filtri, ordine o ricerca si torna a pagina 1 (altrimenti si rischia una pagina vuota)
  useEffect(() => { setPage(1); }, [filtri.chiave, search, ordina.key]);

  // La lente "scaduti"/"scontati"/"gratis"/"senza-cliente" non esiste lato API:
  // sono insiemi che la panoramica ha gia' calcolato. Invece di inventare
  // quattro filtri nuovi sul server, si riusa l'elenco di id che arriva da li'.
  const idsLente = useMemo(() => {
    if (!ov || !lente) return null;
    const a = ov.attention;
    const src = lente === 'senza-cliente' ? a.noClientActive
      : lente === 'scontati' ? a.discounted
      : lente === 'scaduti' ? a.overdue
      : a.zeroRevenue;
    return new Set(src.map((r) => r.id));
  }, [ov, lente]);

  const params = new URLSearchParams();
  if (tipo) params.set('type', tipo);
  if (stato) params.set('status', stato);
  if (search) params.set('search', search);
  params.set('sort', ordina.key);
  // Con una lente accesa si chiede una pagina sola e grande: gli insiemi sono
  // piccoli (qualche unita') e si filtrano qui senza perdere righe per strada.
  params.set('page', String(idsLente ? 1 : page));
  params.set('pageSize', String(idsLente ? 200 : PAGE_SIZE));

  const { data, isLoading } = useQuery({
    queryKey: ['services', tipo, stato, search, ordina.key, page, !!idsLente],
    queryFn: () => api.get<PaginatedServices>(`/services?${params.toString()}`),
  });

  const righe = useMemo(() => {
    const items = data?.items ?? [];
    return idsLente ? items.filter((s) => idsLente.has(s.id)) : items;
  }, [data, idsLente]);

  // Chi ha gia' la fattura del periodo in corso. Arriva dalla panoramica ed e'
  // disponibile solo a chi vede Amministrazione: senza, la colonna non si mostra.
  const fatturati = useMemo(
    () => (ov?.reconciliation ? new Set(ov.reconciliation.invoicedServiceIds) : null),
    [ov],
  );

  // Dalla panoramica all'elenco gia' filtrato: un solo passaggio nell'URL,
  // cosi' "indietro" riporta alla panoramica. L'ordine scelto resta.
  const vaiElenco = (filtro: string) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('scheda', 'elenco');
      for (const k of ['stato', 'tipo', 'solo', 'q']) sp.delete(k);
      sp.set(filtro === 'PAST_DUE' ? 'stato' : 'solo', filtro);
      return sp;
    });
  };

  const filtriAccesi = filtri.attivi + (search.trim() ? 1 : 0);
  // Con una lente accesa le righe sono tutte qui e il totale del server non
  // c'entra piu': si conta quello che si vede.
  const risultati = idsLente ? righe.length : data?.total ?? 0;

  const SCHEDE: Array<{ key: Scheda; label: string; count?: number }> = [
    { key: 'panoramica', label: 'Panoramica' },
    { key: 'elenco', label: 'Elenco', count: ov?.counts.total },
  ];
  // La scheda "Fuori Stripe" compare solo se ci sono davvero canoni da mostrare
  // e se l'utente puo' vederli: una scheda vuota e' peggio di una scheda assente.
  if (ov?.offStripe && ov.offStripe.items.length > 0) {
    SCHEDE.push({ key: 'fuori', label: 'Fuori Stripe', count: ov.offStripe.items.length });
  }

  return (
    <div className="page">
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Abbonamenti</h1>
          <div className="ed-sub">Tutto quello che si incassa a cadenza fissa: hosting, GoDeal, Aura OS e i canoni fatturati a mano.</div>
        </div>
        {/* d-only: sul telefono l'azione principale sta in fondo, dove arriva il pollice. */}
        <div className="flex gap-2 pb-1 d-only">
          <NewMenu
            label="Nuovo abbonamento"
            groups={[
              {
                label: 'Abbonamenti',
                items: [
                  { icon: 'link', title: 'Checkout Stripe', sub: 'Link di pagamento per il cliente', onClick: () => openModal('attachSubscription') },
                  { icon: 'cube', title: 'Abbonamento manuale', sub: 'Non collegato a un provider', onClick: () => openModal('newService') },
                  { icon: 'refresh', title: importStripe.isPending ? 'Importo da Stripe…' : 'Importa da Stripe', sub: 'Scansiona le subscription esistenti', onClick: () => { if (!importStripe.isPending) importStripe.mutate(); } },
                ],
              },
            ]}
          />
          <ActionsMenu
            variant="ghost"
            items={[
              // 'Servizi' e' la CHIAVE del registro in lib/export.ts, non un'etichetta:
              // rinominarla qui farebbe cadere l'export nel ramo "non disponibile".
              // La voce si rinomina la' dentro, che e' fuori da questa cartella.
              { key: 'export', label: 'Esporta', icon: 'export', onClick: () => openModal('export', { what: 'Servizi' }) },
            ]}
          />
        </div>
      </div>

      {/* ── I quattro numeri che si leggono in tre secondi ─────────────────── */}
      {/* withIcon={false}: la fascia vera (.stat-band) non ha icone, e mostrarle
          mentre carica fa saltare la riga appena arrivano i numeri. E' quello
          che fa gia' la lista Progetti. */}
      {ovLoading && !ov ? (
        <SkeletonKPIRow count={4} withIcon={false} />
      ) : (
        <div className="stat-band">
          <div>
            <div className="st-label">Incassiamo al mese</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={ov?.money.mrr ?? 0} format={fmtEur} /></div>
            {/* Il listino si dice solo se e' diverso: altrimenti e' rumore. */}
            <div className="st-delta">
              {ov && ov.money.discount > 0
                ? `a listino sarebbero € ${fmtEur(ov.money.mrrList)}`
                : `${ov?.counts.vivi ?? 0} abbonamenti attivi`}
            </div>
          </div>
          <div>
            <div className="st-label">In un anno</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={ov?.money.arr ?? 0} format={fmtEur} /></div>
            <div className="st-delta">solo da Stripe</div>
          </div>
          <div
            className="st-click"
            role="button"
            tabIndex={0}
            onClick={() => setScheda('panoramica')}
            onKeyDown={(e) => { if (e.key === 'Enter') setScheda('panoramica'); }}
          >
            <div className="st-label">Si rinnova entro {ov?.renewals.windowDays ?? 30} giorni</div>
            <div className="st-value"><AnimatedNumber value={ov?.renewals.next30.count ?? 0} /></div>
            <div className="st-delta">€ {fmtEur(ov?.renewals.next30.amount ?? 0)} in arrivo</div>
          </div>
          <div
            className="st-click"
            role="button"
            tabIndex={0}
            onClick={() => vaiElenco('gratis')}
            onKeyDown={(e) => { if (e.key === 'Enter') vaiElenco('gratis'); }}
          >
            <div className="st-label">Attivi che non incassano</div>
            <div className="st-value"><AnimatedNumber value={ov?.attention.zeroRevenue.length ?? 0} /></div>
            <div className={`st-delta${(ov?.attention.zeroRevenue.length ?? 0) > 0 ? ' text-alert' : ''}`}>
              {(ov?.attention.zeroRevenue.length ?? 0) > 0
                ? `€ ${fmtEur(ov?.attention.zeroRevenueYearlyList ?? 0)} l'anno regalati`
                : 'nessuno'}
            </div>
          </div>
        </div>
      )}

      <div className="page-tabs">
        {SCHEDE.map((t) => (
          <div key={t.key} className="page-tab" aria-selected={scheda === t.key} onClick={() => setScheda(t.key)}>
            {t.label}
            {t.count != null && <span className="c">{t.count}</span>}
          </div>
        ))}
      </div>

      {scheda === 'panoramica' && (
        ov ? <SubscriptionsOverview data={ov} onVaiElenco={vaiElenco} />
           // SkeletonTable produce <tr>: fuori da una tabella React protesta.
           // Mentre la panoramica carica bastano due blocchi grigi.
           : (
             <div className="ed-section sb-cols">
               <div><SkeletonCard titleW={220} blocks={4} /></div>
               <div><SkeletonCard titleW={160} blocks={3} /></div>
             </div>
           )
      )}

      {scheda === 'fuori' && ov && <OffStripeTab data={ov} />}

      {scheda === 'elenco' && (
        <>
          <MobileFilters attivi={filtriAccesi} label="Cerca, ordina e filtra">
            <div className="filterbar filterbar--float mb-4 flex-wrap">
              <div className="tb-search" style={{ width: 280, margin: 0 }}>
                <Icon name="search" size={12} />
                <input
                  aria-label="Cerca fra gli abbonamenti"
                  placeholder="Cerca per cliente, piano, id Stripe…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {/* Stato, tipo e le lenti della panoramica stanno in Filtra per:
                  "Togli i filtri" toglie quelli e lascia la ricerca. */}
              {/* .filterbar .spacer e' gia' flex-1: lo style inline ripeteva la
                  stessa cosa. */}
              <div className="spacer" />
              {/* "1 risultati" e' il singolare sbagliato che si nota. */}
              <span className="mute text-[12px]">
                <span className="mono">{risultati}</span> {risultati === 1 ? 'risultato' : 'risultati'}
              </span>
              <OrdinaFiltra ordina={ordina} filtri={filtri} />
            </div>
          </MobileFilters>

          {/* Quando una lente e' accesa si dice a voce alta cosa si sta guardando:
              altrimenti sembra che la lista abbia perso delle righe. */}
          {lente && (
            <div className="adm-note">
              <Icon name="filter" size={12} className="mute" />
              <span>
                {lente === 'senza-cliente' && <>Solo gli abbonamenti <strong>attivi senza cliente in rubrica</strong>.</>}
                {lente === 'scontati' && <>Solo gli abbonamenti <strong>scontati in modo permanente</strong>.</>}
                {lente === 'scaduti' && <>Solo gli abbonamenti <strong>attivi con il rinnovo già passato</strong>.</>}
                {lente === 'gratis' && <>Solo gli abbonamenti <strong>attivi che non incassano niente</strong>.</>}
                {' '}
                <button className="go" style={{ background: 'none', border: 0, cursor: 'pointer', textDecoration: 'underline', color: 'inherit', padding: 0 }} onClick={() => filtri.set('solo', [])}>
                  Mostra tutti
                </button>
              </span>
            </div>
          )}

          <div className="ed-section">
            {/* tbl--card: sotto i 768px ogni riga diventa una scheda.
                Regole in os-planning/mobile.md */}
            <table className="tbl tbl--ed tbl--card">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Cosa paga</th>
                  <th style={{ width: 110 }}>Tipo</th>
                  <th style={{ width: 120 }}>Stato</th>
                  <th className="num" style={{ width: 110 }}>Quanto</th>
                  <th style={{ width: 110 }}>Ogni quanto</th>
                  <th style={{ width: 120 }}>Prossimo rinnovo</th>
                  {fatturati && <th style={{ width: 90 }}>Fattura</th>}
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {isLoading && !data && <SkeletonTable rows={6} cols={fatturati ? 9 : 8} />}
                {righe.map((s) => {
                  const scontato = s.listAmount > s.amount;
                  const senzaCliente = !s.client;
                  const nomeStripe = (s.metadata?.stripeCustomerName as string) ?? null;
                  return (
                    <tr key={s.id} onClick={() => navigate(`/services/${s.id}`)}>
                      <td data-m="hd">
                        {s.client ? (
                          // Il link alla scheda del cliente: stopPropagation, altrimenti
                          // il click aprirebbe anche il dettaglio dell'abbonamento.
                          <Link
                            to={`/contatti/client/${s.client.id}`}
                            className="sb-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {s.client.legalName}
                          </Link>
                        ) : (
                          <span className="flex items-center gap-2 flex-wrap">
                            <span className="mute">{nomeStripe ?? 'Senza anagrafica'}</span>
                            <Chip tone="warn">Non in rubrica</Chip>
                          </span>
                        )}
                      </td>
                      {/* Niente text-[13px] sui <td>: `.tbl tbody td` fissa 14px
                          con due elementi nel selettore e l'utility, che ne ha
                          uno, perde. Scriverlo faceva credere a una cella piu'
                          piccola che non c'e' mai stata. */}
                      <td data-m="row" data-l="Cosa paga">{s.planLabel}</td>
                      {/* data-m="off": sul telefono il tipo non serve a decidere
                          se aprire la riga — "Hosting Masamune" lo dice gia' il
                          nome del piano due righe sopra. Sul computer la colonna
                          resta dov'era. */}
                      <td data-m="off"><span className="chip-label">{MANAGED_SERVICE_LABEL[s.type]}</span></td>
                      <td data-m="key" data-l="Stato">
                        <Chip tone={statusChipTone(s.status)} dot>{SERVICE_STATUS_LABEL[s.status]}</Chip>
                      </td>
                      <td data-m="key" data-l="Quanto" className="num">
                        {s.amount === 0 ? (
                          <span className="text-alert">gratis</span>
                        ) : (
                          <>
                            <span className="mono">€ {fmtEur(s.amount)}</span>
                            {/* Il listino barrato dice in un colpo d'occhio che c'e' uno sconto perpetuo */}
                            {scontato && <div className="mute text-[11px]" style={{ textDecoration: 'line-through' }}>€ {fmtEur(s.listAmount)}</div>}
                          </>
                        )}
                      </td>
                      <td data-m="key" data-l="Ogni quanto" className="mute">{cicloLabel(s.billingCycle)}</td>
                      <td data-m="key" data-l="Prossimo rinnovo" className="mute mono">{fmtDate(s.currentPeriodEnd)}</td>
                      {fatturati && (
                        // data-m="off": il riscontro fattura/canone e' un
                        // controllo da scrivania, e sul telefono portava la
                        // scheda a sei informazioni. os-planning/mobile.md §3
                        // dice quattro al massimo: restano stato, importo,
                        // cadenza e prossimo rinnovo.
                        <td data-m="off">
                          {s.status === 'ACTIVE' && s.amount > 0 ? (
                            <span className="flex items-center gap-2">
                              <span className={`sb-dot ${fatturati.has(s.id) ? 'on' : 'off'}`} />
                              <span className="mute text-[12px]">{fatturati.has(s.id) ? 'emessa' : 'manca'}</span>
                            </span>
                          ) : (
                            <span className="mute text-[12px]">—</span>
                          )}
                        </td>
                      )}
                      <td data-m="off"><Icon name="chevronR" size={12} className="mute" /></td>
                    </tr>
                  );
                })}
                {!isLoading && righe.length === 0 && (
                  <tr>
                    <td colSpan={fatturati ? 9 : 8} style={{ padding: 0 }}>
                      <EmptyState
                        brand
                        title={filtriAccesi > 0 ? 'Nessun abbonamento trovato' : 'Nessun abbonamento ancora'}
                        description={
                          filtriAccesi > 0
                            ? 'Prova a cambiare i filtri o la ricerca.'
                            : 'Importa le subscription da Stripe oppure crea il primo abbonamento a mano.'
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Con una lente accesa la paginazione non c'entra: le righe sono tutte qui. */}
          {data && !idsLente && (
            <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
          )}

          {/* La riga onesta sulla colonna "Fattura": senza spiegazione un pallino
              vuoto sembra un errore, e invece e' un'informazione precisa. */}
          {fatturati && ov?.reconciliation && (
            <div className="adm-note" style={{ marginTop: 16 }}>
              <Icon name="help" size={12} className="mute" />
              <span>
                <strong>Fattura</strong> dice se per il periodo in corso esiste già una fattura di pari importo
                intestata a quel cliente. L'automazione le emette da{' '}
                {new Date(ov.reconciliation.since).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}:
                per i canoni annuali partiti prima, "manca" vuol dire solo che la fattura è precedente
                all'automazione, non che non sia mai stata fatta.
              </span>
            </div>
          )}
        </>
      )}

      {/* L'azione principale in fondo, dove arriva il pollice (os-planning/mobile.md §4) */}
      <DesktopOnly
        cosa="L'attivazione di un nuovo abbonamento"
        perche="richiede cliente, piano, provider e condizioni di fatturazione; dal telefono puoi controllare e sincronizzare quelli attivi."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newService')}>
            <Icon name="plus" size={12} /> Nuovo abbonamento
          </button>
        </div>
      </DesktopOnly>
    </div>
  );
}
